import React, { useState } from 'react';
import { FiBriefcase, FiEye } from 'react-icons/fi';
import {
  PALETTE,
  Card,
  CardLabel,
  CardRow,
  CardValue,
  ErrText,
  Hint,
  Loading,
  SectionTitle,
  inputStyle,
  primaryBtnStyle,
  smallBtnStyle,
} from '../station/stationTheme';
import type { CorpSummary } from './types';

interface Props {
  corps: CorpSummary[] | null;
  busy: boolean;
  error: string | null;
  applyMsg: string | null;
  onOpenCorp: (corpId: string) => void;
  onApply: (query: string) => void;
  onLeave: () => void;
  /** When set, shows the "Found a corporation" card. Stations pass this; the HUD does not. */
  onFound?: () => void;
  /** Footer hint to nudge HUD users toward a station when they can't found one from here. */
  foundUnavailableHint?: string;
}

export function CorpListView({
  corps,
  busy,
  error,
  applyMsg,
  onOpenCorp,
  onApply,
  onLeave,
  onFound,
  foundUnavailableHint,
}: Props) {
  const [applyQuery, setApplyQuery] = useState('');

  return (
    <>
      {!corps && <Loading />}
      {corps && (
        <>
          <Card>
            <SectionTitle>Your corporations</SectionTitle>
            {corps.length === 0 && <Hint>You are not part of any corporation yet.</Hint>}
            {corps.map((c) => (
              <CardRow key={c.id}>
                <CardLabel>
                  <span style={{ color: PALETTE.LIGHT, fontSize: 11 }}>
                    {c.name} [{c.tag}]
                  </span>
                  {c.pendingApplicationsCount > 0 && (
                    <span
                      style={attentionPillStyle}
                      title={`${c.pendingApplicationsCount} pending application${c.pendingApplicationsCount === 1 ? '' : 's'}`}
                    >
                      {c.pendingApplicationsCount} pending
                    </span>
                  )}
                </CardLabel>
                <CardValue>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    {c.isManager ? (
                      <span style={{ color: PALETTE.TEAL }}>Manager</span>
                    ) : c.isOwner ? (
                      <span style={{ color: PALETTE.GREEN }}>{c.myPercentage}% owner</span>
                    ) : (
                      <span style={{ color: PALETTE.DIM }}>Member</span>
                    )}
                    <button
                      style={viewBtnStyle}
                      onClick={() => onOpenCorp(c.id)}
                      title="View corporation"
                    >
                      <FiEye size={12} />
                      <span>VIEW</span>
                    </button>
                  </span>
                </CardValue>
              </CardRow>
            ))}
          </Card>

          <Card>
            <SectionTitle>Join a corporation</SectionTitle>
            <Hint>
              Apply by corp tag or name. A manager must accept you. Members get the corp chat, the tag on your ship, and protection.
            </Hint>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                value={applyQuery}
                placeholder="tag or name"
                onChange={(e) => setApplyQuery(e.target.value)}
              />
              <button
                style={smallBtnStyle(busy || !applyQuery.trim())}
                disabled={busy || !applyQuery.trim()}
                onClick={() => onApply(applyQuery.trim())}
              >
                APPLY
              </button>
            </div>
            {applyMsg && <Hint style={{ color: PALETTE.GREEN }}>{applyMsg}</Hint>}
            {corps.some((c) => c.isMember) && (
              <button
                style={{ ...smallBtnStyle(false), marginTop: 8 }}
                onClick={onLeave}
                disabled={busy}
              >
                LEAVE MY CORPORATION
              </button>
            )}
          </Card>

          {onFound && (
            <Card>
              <SectionTitle>Found a corporation</SectionTitle>
              <Hint>A corporation is a legal entity that can own stations, buildings and hold credits, separate from any pilot.</Hint>
              <button style={primaryBtnStyle(false)} onClick={onFound}>
                <FiBriefcase size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                FOUND A CORPORATION
              </button>
            </Card>
          )}

          {!onFound && foundUnavailableHint && (
            <Card>
              <SectionTitle>Found a corporation</SectionTitle>
              <Hint>{foundUnavailableHint}</Hint>
            </Card>
          )}

          {error && <ErrText>{error}</ErrText>}
        </>
      )}
    </>
  );
}

const viewBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 8px',
  background: 'transparent',
  color: PALETTE.TEAL,
  border: `1px solid ${PALETTE.TEAL}`,
  borderRadius: 3,
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.08em',
};

const attentionPillStyle: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: 8,
  padding: '1px 6px',
  borderRadius: 8,
  fontSize: 9,
  fontWeight: 700,
  color: PALETTE.DARK,
  background: PALETTE.ORANGE,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
};
